import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { ResolvedDatumTable } from "building-types/datums/datumsTypes";
import type { CipComponentEntity } from "building-types/floors/cipComponentTypes";
import type { MasonryComponentEntity } from "building-types/floors/masonryComponentTypes";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type {
  WallPlaneSubdomain,
  WallPlane,
  WallPlaneDomainOperation,
  WallPlaneDescriptor,
  WallPlaneOpeningDomainOperationsByPlaneHash,
} from "building-types/walls2/wallPlane";
import type { WallPlaneAssemblyEntity, WallPlaneAssemblyLayer } from "building-types/walls2/wallPlaneAssemblyTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import { builtInWallPlaneAssemblies } from "doc/defaults/defaultWallPlaneAssembly";
import { aabb2Init, aabb2Union, aabb2UnionAll } from "geom/aabb2";
import {
  getWallPlaneAssemblyLayerThickness,
  rebuildWallPlaneDescriptorWithAdditionalOperations,
} from "./wallPlaneDescriptor";
import type {
  WallPlaneAssemblyLayerBucketGrid,
  WallPlaneAssemblyLayerPlane,
  WallPlaneFramingLayerBuckets,
} from "./wallPlaneSubPlanesTypes";

type WallPlaneSubPlanes = {
  /**
   * Framing planes in the wall plane, keyed by wall plane hash
   */
  [planeHash: string]: {
    /**
     * Framing layers in the wall plane, keyed by unique layer + offset hash
     */
    [layerHash: string]: WallPlaneAssemblyLayerPlane[];
  };
};

const wallPlaneAssemblyLayerBucketGridMergers = {
  contiguousUIdenticalV: mergeWallPlaneAssemblyLayerBucketGridContiguousUIdenticalV,
  contiguousVIdenticalU: mergeWallPlaneAssemblyLayerBucketGridContiguousVIdenticalU,
};

export function collectWallPlaneSubPlanesByPredicate(
  buildingGraph: BuildingGraph,
  wallPlanes: Table<WallPlane>,
  wallPlaneOpeningDomainOperations: WallPlaneOpeningDomainOperationsByPlaneHash,
  wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  resolvedDatums: ResolvedDatumTable,
  datumAbstractions: Table<DatumAbstraction> = {},
  layerPredicate: (layer: WallPlaneAssemblyLayer) => boolean,
  mergeMode: keyof typeof wallPlaneAssemblyLayerBucketGridMergers,
) {
  const wallMasonryPlanes: WallPlaneSubPlanes = {};

  const allWallPLaneAssemblies = { ...builtInWallPlaneAssemblies, ...wallPlaneAssemblies };

  for (const planeHash in wallPlanes) {
    const plane = wallPlanes[planeHash];
    const buildingPlane = buildingGraph.buildingPlanes[planeHash];
    if (!buildingPlane) {
      continue;
    }

    // partition the wall plane into layer grids, un merged
    const { buckets: assemblyLayersByLayerHash } = collectWallPlaneAssemblyLayerPlaneBuckets(
      buildingGraph,
      plane,
      wallPlaneOpeningDomainOperations[planeHash] ?? [],
      allWallPLaneAssemblies,
      framingComponents,
      masonryComponents,
      cipComponents,
      sheathingComponents,
      resolvedDatums,
      datumAbstractions,
      layerPredicate,
    );

    const planesByLayerHash: Record<string, WallPlaneAssemblyLayerPlane[]> = {};
    for (const [layerHash, bucket] of assemblyLayersByLayerHash.entries()) {
      const wallPlaneAssemblyLayerPlanes = wallPlaneAssemblyLayerBucketGridMergers[mergeMode](bucket);
      planesByLayerHash[layerHash] = wallPlaneAssemblyLayerPlanes;
    }

    wallMasonryPlanes[planeHash] = planesByLayerHash;
  }

  return wallMasonryPlanes;
}

/**
 * Groups occupied bucket cells by arrayIndexU, then splits each column into maximal contiguous V runs (no skipped V).
 * Then merges planes by contiguous U with identical V indices.
 * This produces as set of planes that are contiguous in U and V
 *
 * this is mostly built for framing, but for debug purposes can be used to visualize any layer type
 */
function mergeWallPlaneAssemblyLayerBucketGridContiguousUIdenticalV(
  bucket: WallPlaneAssemblyLayerBucketGrid,
): WallPlaneAssemblyLayerPlane[] {
  const byArrayIndexU = new Map<number, WallPlaneAssemblyBucketColumnCell[]>();

  for (const [v, row] of bucket) {
    for (const [u, plane] of row) {
      const column = byArrayIndexU.get(u);
      const cell: WallPlaneAssemblyBucketColumnCell = { u, v, plane };
      if (column) {
        column.push(cell);
      } else {
        byArrayIndexU.set(u, [cell]);
      }
    }
  }

  const planes: WallPlaneAssemblyLayerPlane[] = [];
  const sortedU = [...byArrayIndexU.keys()].sort((a, b) => a - b);
  for (const u of sortedU) {
    const columnCells = byArrayIndexU.get(u);
    assertIsNotNullOrUndefined(columnCells);
    const vPlanes = mergeBucketColumnCellsIntoContiguousVPlanes(columnCells, u);
    planes.push(...vPlanes);
  }

  return mergeWallPlaneAssemblyLayerPlanesByContiguousUWithIdenticalV(planes);
}

/**
 * Groups occupied bucket cells by arrayIndexV, then splits each row into maximal contiguous U runs (no skipped U).
 * Then merges planes by contiguous V with identical U indices.
 * This produces as set of planes that are contiguous in U and V
 *
 * this is mostly built for framing, but for debug purposes can be used to visualize any layer type
 */
function mergeWallPlaneAssemblyLayerBucketGridContiguousVIdenticalU(
  bucket: WallPlaneAssemblyLayerBucketGrid,
): WallPlaneAssemblyLayerPlane[] {
  const byArrayIndexV = new Map<number, WallPlaneAssemblyBucketColumnCell[]>();

  for (const [v, row] of bucket) {
    for (const [u, plane] of row) {
      const bucketRow = byArrayIndexV.get(v);
      const cell: WallPlaneAssemblyBucketColumnCell = { u, v, plane };
      if (bucketRow) {
        bucketRow.push(cell);
      } else {
        byArrayIndexV.set(v, [cell]);
      }
    }
  }

  const planes: WallPlaneAssemblyLayerPlane[] = [];
  const sortedV = [...byArrayIndexV.keys()].sort((a, b) => a - b);
  for (const v of sortedV) {
    const rowCells = byArrayIndexV.get(v);
    assertIsNotNullOrUndefined(rowCells);
    const uPlanes = mergeBucketRowCellsIntoContiguousUPlanes(rowCells, v);
    planes.push(...uPlanes);
  }

  return mergeWallPlaneAssemblyLayerPlanesByContiguousVWithIdenticalU(planes);
}

function collectWallPlaneAssemblyLayerPlaneBuckets(
  buildingGraph: BuildingGraph,
  wallPlane: WallPlane,
  wallPlaneAdditionalOperations: WallPlaneDomainOperation[],
  wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  resolvedDatums: ResolvedDatumTable,
  datumAbstractions: Table<DatumAbstraction>,
  wallPlaneAssemblyLayerFilter?: (layer: WallPlaneAssemblyLayer) => boolean,
): WallPlaneFramingLayerBuckets {
  let wallDescriptor = wallPlane.descriptor;

  /**
   * If there are no additional operations, we can just use the original descriptor
   * as it already includes the domain operations.
   */
  if (wallPlaneAdditionalOperations.length > 0) {
    wallDescriptor = rebuildWallPlaneDescriptorWithAdditionalOperations(
      wallPlane,
      buildingGraph,
      wallPlaneAdditionalOperations,
      resolvedDatums,
      datumAbstractions,
      wallPlaneAssemblies,
      framingComponents,
      masonryComponents,
      cipComponents,
      sheathingComponents,
    );
  }

  const assemblyLayersByLayerBucketGrid = buildWallPlaneAssemblyLayerBucketGrid(
    wallDescriptor,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
    wallPlaneAssemblyLayerFilter,
  );

  return { buckets: assemblyLayersByLayerBucketGrid, descriptor: wallDescriptor };
}

type WallPlaneAssemblyBucketColumnCell = {
  readonly u: number;
  readonly v: number;
  readonly plane: WallPlaneAssemblyLayerPlane;
};

/**
 * Walk wall-plane subdomains and structural assemblies (left cladding, core, right cladding); bucket each layer plane
 * cell into a sparse (v,u) grid keyed by layer structural hash + mid-plane offset so matching layers at the same offset
 * merge downstream.
 */
function buildWallPlaneAssemblyLayerBucketGrid(
  wallPlaneDescriptor: WallPlaneDescriptor,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  wallPlaneAssemblyLayerFilter?: (layer: WallPlaneAssemblyLayer) => boolean,
): Map<string, WallPlaneAssemblyLayerBucketGrid> {
  const assemblyLayersByLayerHash: Map<string, WallPlaneAssemblyLayerBucketGrid> = new Map();

  function bucketSingletonForLayer(
    domain: WallPlaneSubdomain,
    layer: WallPlaneAssemblyLayer,
    layerThickness: number,
    layerCenterOffset: number,
  ): void {
    if (wallPlaneAssemblyLayerFilter && !wallPlaneAssemblyLayerFilter(layer)) {
      return;
    }

    const layerU = domain.arrayIndexU;
    const layerV = domain.arrayIndexV;

    const singletonPlane: WallPlaneAssemblyLayerPlane = {
      layer,
      layerThickness,
      layerCenterOffset,
      subdomains: [domain],
      subdomainMergedAABB: domain,
      subdomainUIndices: new Set([layerU]),
      subdomainVindices: new Set([layerV]),
    };

    const layerHash = `${hashWallAssemblyLayer(layer)}:${layerCenterOffset}`;
    let bucket = assemblyLayersByLayerHash.get(layerHash);
    if (!bucket) {
      bucket = new Map();
      assemblyLayersByLayerHash.set(layerHash, bucket);
    }

    let row = bucket.get(layerV);
    if (!row) {
      row = new Map();
      bucket.set(layerV, row);
    }
    row.set(layerU, singletonPlane);
  }

  const allDomains = wallPlaneDescriptor.subdomainIndex.all();
  for (const domain of allDomains) {
    if (!domain.wallInfo) {
      continue;
    }

    const {
      wallOffset,
      coreAssembly,
      coreThickness,
      coreDirection,
      leftCladdingAssembly,
      leftCoreToCladdingAirGap,
      rightCladdingAssembly,
      rightCoreToCladdingAirGap,
    } = domain.wallInfo;

    if (!coreAssembly && !leftCladdingAssembly && !rightCladdingAssembly) {
      continue;
    }

    const coreLeft = wallOffset - coreThickness / 2;
    const coreRight = wallOffset + coreThickness / 2;

    let wAlongNormal = coreLeft - leftCoreToCladdingAirGap;
    if (leftCladdingAssembly) {
      for (const layer of sortTableBySortIndex(leftCladdingAssembly.layers)) {
        const layerThickness = getWallPlaneAssemblyLayerThickness(
          layer,
          framingComponents,
          masonryComponents,
          cipComponents,
          sheathingComponents,
        );
        const layerCenterOffset = wAlongNormal - layerThickness / 2;
        bucketSingletonForLayer(domain, layer, layerThickness, layerCenterOffset);
        wAlongNormal -= layerThickness;
      }
    }

    wAlongNormal = coreLeft;
    if (coreAssembly) {
      const sortedLayers = sortTableBySortIndex(coreAssembly.layers);
      if (coreDirection === "RIGHT_TO_LEFT") {
        sortedLayers.reverse();
      }
      for (const layer of sortedLayers) {
        const layerThickness = getWallPlaneAssemblyLayerThickness(
          layer,
          framingComponents,
          masonryComponents,
          cipComponents,
          sheathingComponents,
        );
        const layerCenterOffset = wAlongNormal + layerThickness / 2;
        bucketSingletonForLayer(domain, layer, layerThickness, layerCenterOffset);
        wAlongNormal += layerThickness;
      }
    }

    wAlongNormal = coreRight + rightCoreToCladdingAirGap;
    if (rightCladdingAssembly) {
      for (const layer of sortTableBySortIndex(rightCladdingAssembly.layers)) {
        const layerThickness = getWallPlaneAssemblyLayerThickness(
          layer,
          framingComponents,
          masonryComponents,
          cipComponents,
          sheathingComponents,
        );
        const layerCenterOffset = wAlongNormal + layerThickness / 2;
        bucketSingletonForLayer(domain, layer, layerThickness, layerCenterOffset);
        wAlongNormal += layerThickness;
      }
    }
  }

  return assemblyLayersByLayerHash;
}

export function collectWallPlaneFramingLayerBucketsForWallPlane(
  buildingGraph: BuildingGraph,
  wallPlane: WallPlane,
  wallPlaneAdditionalOperations: WallPlaneDomainOperation[],
  wallPlaneAssemblies: Table<WallPlaneAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
  masonryComponents: Table<MasonryComponentEntity>,
  cipComponents: Table<CipComponentEntity>,
  sheathingComponents: Table<SheathingComponentEntity>,
  resolvedDatums: ResolvedDatumTable,
  datumAbstractions: Table<DatumAbstraction>,
): WallPlaneFramingLayerBuckets {
  return collectWallPlaneAssemblyLayerPlaneBuckets(
    buildingGraph,
    wallPlane,
    wallPlaneAdditionalOperations,
    wallPlaneAssemblies,
    framingComponents,
    masonryComponents,
    cipComponents,
    sheathingComponents,
    resolvedDatums,
    datumAbstractions,
    (layer) => layer.layerType === "FRAMING",
  );
}

/**
 * @TODO WALLS V2 - make layers a doc table and use static ids
 * @NOTE - temporary hash while we put ids on layers
 */
export function hashWallAssemblyLayer(layer: WallPlaneAssemblyLayer): string {
  let layerBaseHash = `${layer.layerType}`;
  if (layer.layerType === "FRAMING") {
    layerBaseHash += `:${layer.framingComponentId}`;
    layerBaseHash += `:${layer.topPlateCount}`;
    layerBaseHash += `:${layer.bottomPlateCount}`;
    layerBaseHash += `:${layer.studSpacing}`;
  } else if (layer.layerType === "MASONRY") {
    layerBaseHash += `:${layer.masonryComponentId}`;
  } else if (layer.layerType === "CAST_IN_PLACE") {
    layerBaseHash += `:${layer.cipComponentId}`;
  } else if (layer.layerType === "SHEATHING") {
    layerBaseHash += `:${layer.sheathingComponentId}`;
  }
  return layerBaseHash;
}

/**
 * Within one U column, group cells into maximal contiguous V-intervals (no skipped V), each as one plane.
 */
function mergeBucketColumnCellsIntoContiguousVPlanes(
  cellsForSingleU: WallPlaneAssemblyBucketColumnCell[],
  columnU: number,
): WallPlaneAssemblyLayerPlane[] {
  if (cellsForSingleU.length === 0) {
    return [];
  }
  const sorted = [...cellsForSingleU].sort((a, b) => {
    if (a.v !== b.v) {
      return a.v - b.v;
    }
    return 0;
  });

  const planesOut: WallPlaneAssemblyLayerPlane[] = [];
  let currentRun: WallPlaneAssemblyBucketColumnCell[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const sameV = curr.v === prev.v;
    const adjacentV = curr.v === prev.v + 1;
    if (sameV || adjacentV) {
      currentRun.push(curr);
    } else {
      planesOut.push(mergeBucketColumnRunIntoLayerPlane(currentRun, columnU));
      currentRun = [curr];
    }
  }
  planesOut.push(mergeBucketColumnRunIntoLayerPlane(currentRun, columnU));

  return planesOut;
}

function mergeBucketColumnRunIntoLayerPlane(
  runCells: WallPlaneAssemblyBucketColumnCell[],
  columnU: number,
): WallPlaneAssemblyLayerPlane {
  const sorted = [...runCells].sort((a, b) => a.v - b.v);
  const firstCell = sorted[0];
  assertIsNotNullOrUndefined(firstCell);
  const base = firstCell.plane;
  return {
    layer: base.layer,
    layerThickness: base.layerThickness,
    layerCenterOffset: base.layerCenterOffset,
    subdomains: sorted.flatMap((c) => c.plane.subdomains),
    subdomainMergedAABB: aabb2UnionAll(sorted.map((c) => c.plane.subdomainMergedAABB)),
    subdomainUIndices: new Set([columnU]),
    subdomainVindices: new Set(sorted.map((c) => c.v)),
  };
}

/**
 * Within one V row, group cells into maximal contiguous U-intervals (no skipped U), each as one plane.
 */
function mergeBucketRowCellsIntoContiguousUPlanes(
  cellsForSingleV: WallPlaneAssemblyBucketColumnCell[],
  rowV: number,
): WallPlaneAssemblyLayerPlane[] {
  if (cellsForSingleV.length === 0) {
    return [];
  }
  const sorted = [...cellsForSingleV].sort((a, b) => {
    if (a.u !== b.u) {
      return a.u - b.u;
    }
    return 0;
  });

  const planesOut: WallPlaneAssemblyLayerPlane[] = [];
  let currentRun: WallPlaneAssemblyBucketColumnCell[] = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1];
    const curr = sorted[i];
    const sameU = curr.u === prev.u;
    const adjacentU = curr.u === prev.u + 1;
    if (sameU || adjacentU) {
      currentRun.push(curr);
    } else {
      planesOut.push(mergeBucketRowRunIntoLayerPlane(currentRun, rowV));
      currentRun = [curr];
    }
  }
  planesOut.push(mergeBucketRowRunIntoLayerPlane(currentRun, rowV));

  return planesOut;
}

function mergeBucketRowRunIntoLayerPlane(
  runCells: WallPlaneAssemblyBucketColumnCell[],
  rowV: number,
): WallPlaneAssemblyLayerPlane {
  const sorted = [...runCells].sort((a, b) => a.u - b.u);
  const firstCell = sorted[0];
  assertIsNotNullOrUndefined(firstCell);
  const base = firstCell.plane;
  return {
    layer: base.layer,
    layerThickness: base.layerThickness,
    layerCenterOffset: base.layerCenterOffset,
    subdomains: sorted.flatMap((c) => c.plane.subdomains),
    subdomainMergedAABB: aabb2UnionAll(sorted.map((c) => c.plane.subdomainMergedAABB)),
    subdomainUIndices: new Set(sorted.map((c) => c.u)),
    subdomainVindices: new Set([rowV]),
  };
}

/**
 * Merge planes across U only when vIndices match exactly; within each V-key group, U runs must be contiguous (no skipped U).
 */
function mergeWallPlaneAssemblyLayerPlanesByContiguousUWithIdenticalV(
  planes: WallPlaneAssemblyLayerPlane[],
): WallPlaneAssemblyLayerPlane[] {
  const byVKey = new Map<string, WallPlaneAssemblyLayerPlane[]>();
  for (const plane of planes) {
    const key = [...plane.subdomainVindices].sort((a, b) => a - b).join(",");
    const list = byVKey.get(key) ?? [];
    list.push(plane);
    byVKey.set(key, list);
  }

  const mergedPlanes: WallPlaneAssemblyLayerPlane[] = [];
  const sortedVKeys = [...byVKey.keys()].sort(compareAssemblyLayerPlaneVIndicesKeys);

  for (const vKey of sortedVKeys) {
    const group = byVKey.get(vKey);
    assertIsNotNullOrUndefined(group);

    const planesWithMinU = group.map((plane) => {
      let minU = Number.POSITIVE_INFINITY;
      for (const u of plane.subdomainUIndices) {
        minU = Math.min(minU, u);
      }
      return { minU, plane };
    });
    planesWithMinU.sort((a, b) => a.minU - b.minU);

    const firstEntry = planesWithMinU[0];
    assertIsNotNullOrUndefined(firstEntry);
    let currentRun: WallPlaneAssemblyLayerPlane[] = [firstEntry.plane];

    for (let i = 1; i < planesWithMinU.length; i++) {
      const prev = planesWithMinU[i - 1];
      const curr = planesWithMinU[i];
      const sameU = curr.minU === prev.minU;
      const adjacentU = curr.minU === prev.minU + 1;
      if (sameU || adjacentU) {
        currentRun.push(curr.plane);
      } else {
        mergedPlanes.push(mergeWallPlaneAssemblyLayerPlanes(currentRun));
        currentRun = [curr.plane];
      }
    }
    mergedPlanes.push(mergeWallPlaneAssemblyLayerPlanes(currentRun));
  }

  return mergedPlanes;
}

/**
 * Merge planes across V only when uIndices match exactly; within each U-key group, V runs must be contiguous (no skipped V).
 */
function mergeWallPlaneAssemblyLayerPlanesByContiguousVWithIdenticalU(
  planes: WallPlaneAssemblyLayerPlane[],
): WallPlaneAssemblyLayerPlane[] {
  const byUKey = new Map<string, WallPlaneAssemblyLayerPlane[]>();
  for (const plane of planes) {
    const key = [...plane.subdomainUIndices].sort((a, b) => a - b).join(",");
    const list = byUKey.get(key) ?? [];
    list.push(plane);
    byUKey.set(key, list);
  }

  const mergedPlanes: WallPlaneAssemblyLayerPlane[] = [];
  const sortedUKeys = [...byUKey.keys()].sort(compareAssemblyLayerPlaneUIndicesKeys);

  for (const uKey of sortedUKeys) {
    const group = byUKey.get(uKey);
    assertIsNotNullOrUndefined(group);

    const planesWithMinV = group.map((plane) => {
      let minV = Number.POSITIVE_INFINITY;
      for (const v of plane.subdomainVindices) {
        minV = Math.min(minV, v);
      }
      return { minV, plane };
    });
    planesWithMinV.sort((a, b) => a.minV - b.minV);

    const firstEntry = planesWithMinV[0];
    assertIsNotNullOrUndefined(firstEntry);
    let currentRun: WallPlaneAssemblyLayerPlane[] = [firstEntry.plane];

    for (let i = 1; i < planesWithMinV.length; i++) {
      const prev = planesWithMinV[i - 1];
      const curr = planesWithMinV[i];
      const sameV = curr.minV === prev.minV;
      const adjacentV = curr.minV === prev.minV + 1;
      if (sameV || adjacentV) {
        currentRun.push(curr.plane);
      } else {
        mergedPlanes.push(mergeWallPlaneAssemblyLayerPlanes(currentRun));
        currentRun = [curr.plane];
      }
    }
    mergedPlanes.push(mergeWallPlaneAssemblyLayerPlanes(currentRun));
  }

  return mergedPlanes;
}

function compareAssemblyLayerPlaneVIndicesKeys(ka: string, kb: string): number {
  const aParts = ka.length === 0 ? [] : ka.split(",").map(Number);
  const bParts = kb.length === 0 ? [] : kb.split(",").map(Number);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const av = aParts[i];
    const bv = bParts[i];
    if (av === undefined && bv === undefined) {
      return 0;
    }
    if (av === undefined) {
      return -1;
    }
    if (bv === undefined) {
      return 1;
    }
    if (av !== bv) {
      return av - bv;
    }
  }
  return 0;
}

function compareAssemblyLayerPlaneUIndicesKeys(ka: string, kb: string): number {
  const aParts = ka.length === 0 ? [] : ka.split(",").map(Number);
  const bParts = kb.length === 0 ? [] : kb.split(",").map(Number);
  const len = Math.max(aParts.length, bParts.length);
  for (let i = 0; i < len; i++) {
    const av = aParts[i];
    const bv = bParts[i];
    if (av === undefined && bv === undefined) {
      return 0;
    }
    if (av === undefined) {
      return -1;
    }
    if (bv === undefined) {
      return 1;
    }
    if (av !== bv) {
      return av - bv;
    }
  }
  return 0;
}

function mergeWallPlaneAssemblyLayerPlanes(planes: WallPlaneAssemblyLayerPlane[]): WallPlaneAssemblyLayerPlane {
  const first = planes[0];

  assertIsNotNullOrUndefined(first);

  const subdomains: WallPlaneSubdomain[] = [];
  const uIndices = new Set<number>();
  const vIndices = new Set<number>();
  let aabb = aabb2Init();

  for (const plane of planes) {
    subdomains.push(...plane.subdomains);
    aabb = aabb2Union(aabb, plane.subdomainMergedAABB);

    for (const u of plane.subdomainUIndices) {
      uIndices.add(u);
    }
    for (const v of plane.subdomainVindices) {
      vIndices.add(v);
    }
  }

  return {
    layer: first.layer,
    layerThickness: first.layerThickness,
    layerCenterOffset: first.layerCenterOffset,
    subdomains,
    subdomainMergedAABB: aabb,
    subdomainUIndices: uIndices,
    subdomainVindices: vIndices,
  };
}
